import React, { useState, useCallback } from 'react';
import axios from 'axios';
import {
  X, Check, XCircle, AlertTriangle, Clock, CheckCircle2,
  FileSignature, MessageSquare, RotateCcw, Shield, Eye,
} from 'lucide-react';
import { theme, cardStyle, inputStyle, btnStyle } from '../theme';
import SignaturePad from './SignaturePad';

// ═══════════════════════════════════════════════════════════════════
//  OnboardingHrPanel — HR verification UI.
//
//  Tabs:
//    1. HR File Checklist (A-T) — Y / N / NA + notes per row.
//    2. Section-by-section signoff — HR views what staff filled and
//       stamps each section verified.
//    3. Overall decision — approve / reject / reopen + HR signature.
// ═══════════════════════════════════════════════════════════════════

const TABS = [
  { key: 'checklist', label: 'HR Checklist (A-T)', icon: Shield },
  { key: 'sections',  label: 'Section Review',      icon: Eye },
  { key: 'decision',  label: 'Decision & Signoff',  icon: FileSignature },
];

const STATUS_CHIPS = [
  { v: 'Y',  l: 'Yes',  color: theme.success },
  { v: 'N',  l: 'No',   color: theme.danger  },
  { v: 'NA', l: 'N/A',  color: theme.textMuted },
];

const SECTION_ROWS = [
  { key: 'profile',                  label: 'Profile sheet' },
  { key: 'addresses',                label: 'Address & emergency' },
  { key: 'educations',               label: 'Education' },
  { key: 'workHistory',              label: 'Work history' },
  { key: 'appointmentLetter',        label: 'Appointment letter' },
  { key: 'confidentialityAgreement', label: 'Confidentiality & NDA' },
  { key: 'joiningInduction',         label: 'Joining & induction' },
  { key: 'authorizationLetter',      label: 'Background check auth' },
  { key: 'bloodTestConsent',         label: 'Blood test consent' },
  { key: 'healthCheckup',            label: 'Annual health check' },
  { key: 'medicalCertificate',       label: 'Medical certificate' },
  { key: 'hepatitisBDeclaration',    label: 'Hepatitis-B declaration' },
  { key: 'noCriminalAffidavit',      label: 'No-criminal affidavit' },
  { key: 'employerReferenceCheck',   label: 'Ex-employer reference' },
  { key: 'telephonicReferenceCheck', label: 'Telephonic reference' },
  { key: 'credentialsVerification',  label: 'Credentials' },
  { key: 'jobDescription',           label: 'Job description' },
  { key: 'trainingLog',              label: 'Training log' },
  { key: 'appraisalLetter',          label: 'Appraisal letter' },
  { key: 'performanceAppraisal',     label: 'Performance appraisal' },
];

const OnboardingHrPanel = ({ API_BASE, currentUser, record, onClose, onUpdate }) => {
  const [tab, setTab] = useState('checklist');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [showSigFor, setShowSigFor] = useState(null);     // 'decision' for HR final signature
  const [decision, setDecision] = useState({
    verdict: '',
    comments: record?.hrVerification?.comments || '',
    rejectionReason: record?.hrVerification?.rejectionReason || '',
    hrSignature: record?.hrVerification?.hrSignature || '',
  });
  const [showSection, setShowSection] = useState(null);

  const show = (t) => { setMsg(t); setTimeout(() => setMsg(''), 2500); };

  const tickItem = useCallback(async (code, status, notes) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API_BASE}/onboarding/${record._id}/hr-item/${code}`, {
        status, notes, verifiedBy: currentUser?.fullName || currentUser?.username,
      });
      onUpdate && onUpdate(res.data);
      show('Saved');
    } catch (err) { show(err.response?.data?.error || err.message); }
    finally { setBusy(false); }
  }, [API_BASE, record?._id, currentUser, onUpdate]);

  const signoffSection = async (secKey, verified, notes) => {
    try {
      const res = await axios.post(`${API_BASE}/onboarding/${record._id}/hr-section/${secKey}`, {
        verified, notes, by: currentUser?.fullName || currentUser?.username,
      });
      onUpdate && onUpdate(res.data);
      show('Section stamped');
    } catch (err) { show(err.response?.data?.error || err.message); }
  };

  const submitDecision = async () => {
    if (!decision.verdict) return show('Approve / reject choose karo');
    if (decision.verdict === 'reject' && !decision.rejectionReason) return show('Rejection reason zaroori hai');
    try {
      const res = await axios.post(`${API_BASE}/onboarding/${record._id}/hr-decision`, {
        decision: decision.verdict,
        comments: decision.comments,
        rejectionReason: decision.rejectionReason,
        hrSignature: decision.hrSignature,
        verifiedBy: currentUser?.fullName || currentUser?.username,
      });
      onUpdate && onUpdate(res.data);
      show('Decision saved');
      if (decision.verdict === 'approve' || decision.verdict === 'reject') {
        setTimeout(() => onClose && onClose(), 700);
      }
    } catch (err) { show(err.response?.data?.error || err.message); }
  };

  const saveSig = (d) => { setDecision(prev => ({ ...prev, hrSignature: d })); setShowSigFor(null); };

  const hrList = record?.hrChecklist || [];
  const form = record?.form || {};
  const sectionSignoff = record?.hrVerification?.sectionSignoff || {};

  // ─── TAB 1 — HR A-T checklist ───
  const renderChecklist = () => (
    <div>
      <div style={{ ...cardStyle, padding: 10, marginBottom: 10, fontSize: 12.5, background: theme.sectionBlue || '#f0f9ff', color: theme.textMuted }}>
        HR File Checklist — paper HR file mein jo Y/N/NA bharna hai, wohi yahan ticks karna hai.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '40px 1fr 200px 1fr', gap: 6, alignItems: 'center', fontSize: 11.5, color: theme.textMuted, fontWeight: 700, padding: '4px 8px' }}>
        <div>Code</div><div>Content</div><div>Status</div><div>Notes</div>
      </div>
      {hrList.map(item => (
        <div key={item.code} style={{ display: 'grid', gridTemplateColumns: '40px 1fr 200px 1fr', gap: 6, alignItems: 'center', padding: '6px 8px', borderBottom: `1px solid ${theme.borderLight}` }}>
          <div style={{ fontWeight: 800, color: theme.navy }}>{item.code}</div>
          <div style={{ fontSize: 12.5, color: theme.navy }}>{item.label}</div>
          <div style={{ display: 'flex', gap: 4 }}>
            {STATUS_CHIPS.map(c => (
              <button key={c.v} onClick={() => tickItem(item.code, c.v, item.notes)}
                disabled={busy}
                style={{
                  padding: '4px 9px', borderRadius: 5, border: `1px solid ${item.status === c.v ? c.color : theme.border}`,
                  background: item.status === c.v ? c.color + '22' : 'white',
                  color: item.status === c.v ? c.color : theme.textMuted,
                  fontWeight: 700, fontSize: 11.5, cursor: 'pointer',
                }}>
                {c.l}
              </button>
            ))}
          </div>
          <input placeholder="notes…" defaultValue={item.notes || ''} onBlur={e => { if (e.target.value !== item.notes) tickItem(item.code, item.status, e.target.value); }}
            style={{ ...inputStyle, padding: '5px 8px', fontSize: 12 }}/>
        </div>
      ))}
      <div style={{ marginTop: 10, fontSize: 12, color: theme.textMuted }}>
        Verified: {hrList.filter(i => i.status === 'Y' || i.status === 'NA').length} / {hrList.length}
      </div>
    </div>
  );

  // ─── TAB 2 — per-section signoff ───
  const renderSections = () => (
    <div>
      <div style={{ ...cardStyle, padding: 10, marginBottom: 10, fontSize: 12.5, background: theme.sectionMint || '#f0fdf4', color: theme.textMuted }}>
        Har section preview karke "Verify" dabao. Sab OK ho to Decision tab me jao.
      </div>
      {SECTION_ROWS.map(({ key, label }) => {
        const s = sectionSignoff[key];
        return (
          <div key={key} style={{ ...cardStyle, padding: 10, marginBottom: 6, display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 10, alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 700, color: theme.navy, fontSize: 13.5 }}>{label}</div>
              <div style={{ fontSize: 11.5, color: theme.textSubtle }}>
                {s?.verified ? `Verified by ${s.by || '—'} on ${s.at ? new Date(s.at).toLocaleDateString('en-IN') : ''}` : 'Not yet verified'}
                {s?.notes ? ` · "${s.notes}"` : ''}
              </div>
            </div>
            <button onClick={() => setShowSection(key)} style={{ ...btnStyle, padding: '6px 10px', fontSize: 12, background: 'white', color: theme.primary, border: `1px solid ${theme.primary}` }}>
              <Eye size={13}/> View
            </button>
            <button onClick={() => signoffSection(key, !s?.verified, s?.notes || '')}
              style={{
                ...btnStyle, padding: '6px 10px', fontSize: 12,
                background: s?.verified ? theme.success : theme.warning,
              }}>
              {s?.verified ? <><Check size={13}/> Verified</> : <><Clock size={13}/> Mark verified</>}
            </button>
          </div>
        );
      })}
      {showSection && (
        <SectionPreviewModal form={form} sectionKey={showSection} onClose={() => setShowSection(null)}
          onSave={(notes) => { signoffSection(showSection, true, notes); setShowSection(null); }}/>
      )}
    </div>
  );

  // ─── TAB 3 — decision ───
  const renderDecision = () => (
    <div>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: theme.textMuted, marginBottom: 6 }}>Verdict</label>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {[
          { v: 'approve', l: 'Approve', ic: CheckCircle2, c: theme.success },
          { v: 'reject',  l: 'Reject — needs fixes', ic: XCircle, c: theme.danger  },
          { v: 'reopen',  l: 'Reopen for edits', ic: RotateCcw, c: theme.warning },
        ].map(({ v, l, ic: Ic, c }) => (
          <button key={v} onClick={() => setDecision(prev => ({ ...prev, verdict: v }))} style={{
            padding: '10px 14px', borderRadius: 8, border: `2px solid ${decision.verdict === v ? c : theme.border}`,
            background: decision.verdict === v ? c + '18' : 'white', color: decision.verdict === v ? c : theme.textMuted,
            fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Ic size={15}/> {l}
          </button>
        ))}
      </div>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: theme.textMuted, marginBottom: 4 }}>HR comments (optional)</label>
      <textarea style={{ ...inputStyle, minHeight: 60 }} value={decision.comments} onChange={e => setDecision(prev => ({ ...prev, comments: e.target.value }))}/>

      {decision.verdict === 'reject' && (
        <>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: theme.danger, margin: '10px 0 4px' }}>Rejection reason (required)</label>
          <textarea style={{ ...inputStyle, minHeight: 50, borderColor: theme.danger }} value={decision.rejectionReason} onChange={e => setDecision(prev => ({ ...prev, rejectionReason: e.target.value }))}/>
        </>
      )}

      <div style={{ marginTop: 10 }}>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: theme.textMuted, marginBottom: 4 }}>HR signature</label>
        {decision.hrSignature && <img src={decision.hrSignature} alt="sig" style={{ maxHeight: 60, border: '1px solid ' + theme.border, borderRadius: 6, marginBottom: 4, background: 'white' }}/>}
        <div>
          <button type="button" onClick={() => setShowSigFor('decision')} style={{ ...btnStyle, padding: '6px 12px', fontSize: 12 }}>
            {decision.hrSignature ? 'Re-sign' : 'Sign here'}
          </button>
        </div>
      </div>

      <button onClick={submitDecision} disabled={!decision.verdict} style={{ ...btnStyle, padding: '10px 18px', marginTop: 16, background: theme.success, fontSize: 14 }}>
        <FileSignature size={15}/> Save decision
      </button>

      {record?.hrVerification?.verifiedAt && (
        <div style={{ marginTop: 14, fontSize: 12, color: theme.textMuted }}>
          Last action: {new Date(record.hrVerification.verifiedAt).toLocaleString('en-IN')} by {record.hrVerification.verifiedBy || '—'}
        </div>
      )}
    </div>
  );

  return (
    <div style={modalBg}>
      <div style={modalBox} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: `1px solid ${theme.border}`, background: theme.primary + '08' }}>
          <Shield size={20} color={theme.primary}/>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, color: theme.navy }}>HR Verification — {record?.fullName}</h3>
            <div style={{ fontSize: 12, color: theme.textMuted }}>
              Stage <b style={{ color: theme.info }}>{record?.submission?.stage}</b> · Form {record?.formCompletionPct || 0}% filled
            </div>
          </div>
          {msg && <span style={{ fontSize: 12, color: theme.info, fontWeight: 600 }}>{msg}</span>}
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={22}/></button>
        </div>

        <div style={{ display: 'flex', borderBottom: `1px solid ${theme.border}`, background: 'white' }}>
          {TABS.map(({ key, label, icon: Ic }) => (
            <button key={key} onClick={() => setTab(key)} style={{
              padding: '10px 14px', border: 'none', background: 'transparent',
              borderBottom: `3px solid ${tab === key ? theme.primary : 'transparent'}`,
              color: tab === key ? theme.primary : theme.textMuted,
              fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <Ic size={14}/> {label}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
          {tab === 'checklist' && renderChecklist()}
          {tab === 'sections'  && renderSections()}
          {tab === 'decision'  && renderDecision()}
        </div>
      </div>

      {showSigFor && <SignaturePad onSave={saveSig} onClose={() => setShowSigFor(null)}/>}
    </div>
  );
};

// ─── Read-only preview of a single section (used in Section Review tab) ───
const SectionPreviewModal = ({ form, sectionKey, onClose, onSave }) => {
  const [notes, setNotes] = useState('');
  const data = form[sectionKey] || form[sectionKey === 'addresses' ? 'presentAddress' : sectionKey];
  return (
    <div style={{ ...modalBg, zIndex: 10100 }}>
      <div style={{ ...modalBox, maxWidth: 720, height: '80vh' }}>
        <div style={{ padding: 14, borderBottom: `1px solid ${theme.border}`, display: 'flex', alignItems: 'center' }}>
          <h4 style={{ margin: 0, flex: 1 }}>Preview — {sectionKey}</h4>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20}/></button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
          {sectionKey === 'addresses' ? (
            <>
              <h5>Present</h5><pre style={pre}>{JSON.stringify(form.presentAddress || {}, null, 2)}</pre>
              <h5>Permanent</h5><pre style={pre}>{JSON.stringify(form.permanentAddress || {}, null, 2)}</pre>
              <h5>Emergency</h5><pre style={pre}>{JSON.stringify(form.emergencyContact || {}, null, 2)}</pre>
            </>
          ) : (
            <pre style={pre}>{JSON.stringify(data || '—', null, 2)}</pre>
          )}
          <label style={{ display: 'block', fontSize: 12, color: theme.textMuted, marginTop: 8 }}>Verification notes</label>
          <textarea style={{ ...inputStyle, minHeight: 50 }} value={notes} onChange={e => setNotes(e.target.value)}/>
        </div>
        <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: `1px solid ${theme.border}` }}>
          <button onClick={onClose} style={{ ...btnStyle, background: 'white', color: theme.navy, border: `1px solid ${theme.border}`, padding: '8px 14px' }}>Cancel</button>
          <div style={{ flex: 1 }}/>
          <button onClick={() => onSave(notes)} style={{ ...btnStyle, background: theme.success, padding: '8px 14px' }}>
            <Check size={14}/> Mark verified
          </button>
        </div>
      </div>
    </div>
  );
};

const pre = { background: theme.sectionCream || '#fffbeb', padding: 10, borderRadius: 6, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' };
const modalBg = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 10 };
const modalBox = {
  background: 'white', borderRadius: 12, width: '100%', maxWidth: 960,
  height: '88vh', display: 'flex', flexDirection: 'column',
  boxShadow: '0 12px 50px rgba(0,0,0,0.35)', overflow: 'hidden',
};

export default OnboardingHrPanel;
